<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  visits: { type: Array, required: true },
  students: { type: Array, required: true },
  offDays: { type: Array, required: true },
  deleteVisit: { type: Function, required: true },
  editVisit: { type: Function, required: true },
  editOffDay: { type: Function, required: true },
  deleteOffDay: { type: Function, required: true },
  printVisitLog: { type: Function, required: true },
});

const OFF_DAY_LABELS = { holiday: 'School holiday', out: 'Out' };

const selectedStudentId = ref('');

const studentNameById = computed(() => {
  const map = {};
  for (const student of props.students) map[student.id] = student.name;
  return map;
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  timeStyle: 'short',
});
const dayFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});
const monthFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'long',
  year: 'numeric',
});

function formatTime(isoString) {
  return timeFormatter.format(new Date(isoString));
}

// Visits logged together (via the multi-student picker) share an exact
// seen_at/subject/duration, so grouping on that combination reconstructs
// which students were seen together in the same session. Sessions are built
// from every visit (not just the filtered student's) so a group session
// still shows who else was there even when filtered to one student.
const allSessions = computed(() => {
  const sessionsByKey = new Map();
  for (const visit of props.visits) {
    const key = `${visit.seen_at}|${visit.subject}|${visit.duration_minutes}`;
    let session = sessionsByKey.get(key);
    if (!session) {
      session = {
        seenAt: visit.seen_at,
        subject: visit.subject,
        durationMinutes: visit.duration_minutes,
        notes: visit.notes,
        entries: [],
      };
      sessionsByKey.set(key, session);
    }
    session.entries.push({
      visit,
      studentName: studentNameById.value[visit.student_id] ?? 'Unknown student',
    });
  }
  return [...sessionsByKey.values()];
});

const monthGroups = computed(() => {
  const sessions = allSessions.value.filter(
    (session) =>
      !selectedStudentId.value ||
      session.entries.some(
        (e) => e.visit.student_id === selectedStudentId.value,
      ),
  );

  const sessionsByDayKey = new Map();
  for (const session of sessions) {
    const d = new Date(session.seenAt);
    const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (!sessionsByDayKey.has(dayKey)) sessionsByDayKey.set(dayKey, []);
    sessionsByDayKey.get(dayKey).push(session);
  }

  // Off days are day-level (not tied to a student), so they show regardless
  // of the student filter and independent of whether any visits happened.
  // A day can have more than one (e.g. logged twice, or holiday + out).
  const offDaysByDayKey = new Map();
  for (const offDay of props.offDays) {
    const [year, month, date] = offDay.date.split('-').map(Number);
    const dayKey = `${year}-${month - 1}-${date}`;
    if (!offDaysByDayKey.has(dayKey)) offDaysByDayKey.set(dayKey, []);
    offDaysByDayKey.get(dayKey).push(offDay);
  }

  const dayKeys = new Set([
    ...sessionsByDayKey.keys(),
    ...offDaysByDayKey.keys(),
  ]);
  const days = [...dayKeys]
    .map((dayKey) => {
      const [year, month, date] = dayKey.split('-').map(Number);
      return {
        key: dayKey,
        date: new Date(year, month, date),
        // Within a day, read top-to-bottom in the order the day happened.
        sessions: (sessionsByDayKey.get(dayKey) ?? []).sort(
          (a, b) => new Date(a.seenAt) - new Date(b.seenAt),
        ),
        offDays: offDaysByDayKey.get(dayKey) ?? [],
      };
    })
    // Chronological (oldest first) — this is also the print order, since
    // print forces .months/.day-list to display:block (see the @media
    // print rule below), so there's no flex-direction left to reverse with
    // for screen there. Screen instead flips to most-recent-first via
    // flex-direction: column-reverse, so both orders come from one sort.
    .sort((a, b) => a.date - b.date);

  const months = [];
  let currentMonth = null;
  for (const day of days) {
    const monthKey = `${day.date.getFullYear()}-${day.date.getMonth()}`;
    if (!currentMonth || currentMonth.key !== monthKey) {
      currentMonth = {
        key: monthKey,
        label: monthFormatter.format(day.date),
        days: [],
      };
      months.push(currentMonth);
    }
    currentMonth.days.push({ ...day, label: dayFormatter.format(day.date) });
  }

  return months;
});


</script>

<template>
  <div class="filter-bar no-print">
    <wa-select
      :value="selectedStudentId"
      label="Student"
      placeholder="All students"
      with-clear
      @input="selectedStudentId = $event.target.value"
    >
      <wa-option
        v-for="student in students"
        :key="student.id"
        :value="student.id"
      >
        {{ student.name }}
      </wa-option>
    </wa-select>
    <wa-button
      appearance="outlined"
      class="print-button"
      :disabled="!visits.length"
      aria-label="Print visit log"
      @click="printVisitLog"
    >
      <wa-icon name="print"></wa-icon>
    </wa-button>
  </div>
  <p v-if="selectedStudentId" class="filter-label">
    Showing: {{ studentNameById[selectedStudentId] ?? 'Unknown student' }}
  </p>

  <p v-if="!monthGroups.length" class="empty">No visits logged yet.</p>

  <div v-else class="months">
    <section v-for="month in monthGroups" :key="month.key" class="month">
      <h2>{{ month.label }}</h2>

      <div class="day-list">
        <div v-for="day in month.days" :key="day.key" class="day">
          <h3>{{ day.label }}</h3>
          <wa-callout
            v-for="offDay in day.offDays"
            :key="offDay.id"
            :variant="offDay.reason_type === 'holiday' ? 'brand' : 'neutral'"
            class="off-day"
          >
            <div class="off-day-body">
              <span>
                <strong>{{ OFF_DAY_LABELS[offDay.reason_type] }}</strong>
                <template v-if="offDay.note"> — {{ offDay.note }}</template>
              </span>
              <span class="row-actions no-print">
                <wa-button
                  size="s"
                  appearance="plain"
                  @click="editOffDay(offDay)"
                  >Edit</wa-button
                >
                <wa-button
                  size="s"
                  appearance="plain"
                  variant="danger"
                  @click="deleteOffDay(offDay.id)"
                >
                  Remove
                </wa-button>
              </span>
            </div>
          </wa-callout>
          <table v-if="day.sessions.length" class="day-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Subject</th>
                <th>Students</th>
                <th>Notes</th>
                <th class="no-print">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="session in day.sessions"
                :key="`${session.seenAt}|${session.subject}|${session.durationMinutes}`"
              >
                <td>
                  {{
                    session.subject === 'Absent'
                      ? 'All day'
                      : formatTime(session.seenAt)
                  }}
                </td>
                <td>
                  {{ session.subject }}
                  <span v-if="session.durationMinutes" class="duration"
                    >({{ session.durationMinutes }} min)</span
                  >
                </td>
                <td>
                  <wa-tag
                    v-for="entry in session.entries"
                    :key="entry.visit.id"
                    size="s"
                    with-remove
                    class="student-tag"
                    @wa-remove="deleteVisit(entry.visit.id, entry.studentName)"
                  >
                    {{ entry.studentName }}
                  </wa-tag>
                </td>
                <td class="notes">
                  <span class="notes-label">Notes: </span>{{ session.notes }}
                </td>
                <td class="no-print">
                  <wa-button
                    size="s"
                    appearance="outlined"
                    @click="editVisit(session.entries[0].visit)"
                    >Edit</wa-button
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.empty {
  color: var(--wa-color-text-quiet);
  text-align: center;
}

.filter-bar {
  display: flex;
  align-items: flex-end;
  gap: var(--wa-space-s);
  margin-bottom: var(--wa-space-s);
}

.filter-bar wa-select {
  flex: 1;
  max-width: 20rem;
}

.filter-label {
  font-weight: 600;
  margin: 0 0 var(--wa-space-m);
}

.months {
  display: flex;
  /* Data is sorted chronologically (oldest first) so print — which forces
     display:block below, dropping flex-direction — reads top-to-bottom in
     the order the visits happened. Screen flips to newest-first here. */
  flex-direction: column-reverse;
  gap: var(--wa-space-l);
}

.month h2 {
  font-size: var(--wa-font-size-m);
  margin: 0 0 var(--wa-space-s);
  border-bottom: solid 2px var(--wa-color-surface-border);
  padding-bottom: var(--wa-space-2xs);
}

.day-list {
  display: flex;
  flex-direction: column-reverse;
  gap: var(--wa-space-m);
}

.day {
  margin-bottom: var(--wa-space-m);
}

.day:last-child {
  padding-top: 0;
  border-top: none;
}

.day h3 {
  font-size: var(--wa-font-size-s);
  font-weight: 700;
  margin: 0 0 var(--wa-space-s);
  padding-bottom: var(--wa-space-2xs);
  color: var(--wa-color-text-normal);
  border-bottom: solid 1px var(--wa-color-neutral-fill-loud);
}

.off-day {
  margin-bottom: var(--wa-space-s);
}

.off-day-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--wa-space-s);
}

.off-day .row-actions {
  display: flex;
  gap: var(--wa-space-2xs);
  flex-shrink: 0;
}

.day-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--wa-font-size-s);
}

.day-table th,
.day-table td {
  text-align: left;
  vertical-align: top;
  padding: var(--wa-space-2xs) var(--wa-space-s) var(--wa-space-2xs) 0;
  border-bottom: solid 1px var(--wa-color-surface-border);
}

.day-table th {
  font-weight: 600;
  color: var(--wa-color-text-quiet);
}

.duration {
  color: var(--wa-color-text-quiet);
}

.student-tag {
  margin: 2px var(--wa-space-2xs) 2px 0;
}

.notes {
  color: var(--wa-color-text-quiet);
  white-space: pre-wrap;
}

.notes-label {
  display: none;
  font-weight: 600;
  color: var(--wa-color-text-normal);
}

@media (max-width: 640px) {
  .day-table thead {
    display: none;
  }

  .day-table,
  .day-table tbody,
  .day-table tr {
    display: block;
    width: 100%;
  }

  .day-table tr {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: var(--wa-space-s);
    row-gap: var(--wa-space-2xs);
    padding-block: var(--wa-space-s);
  }

  .day-table td {
    border-bottom: none;
    padding: 0;
  }

  .day-table td.notes {
    order: 2;
    flex: 1 1 100%;
    padding: var(--wa-space-2xs) var(--wa-space-s);
    background: var(--wa-color-neutral-fill-quiet);
    border: solid 1px var(--wa-color-surface-border);
    border-radius: var(--wa-border-radius-s);
  }

  .notes-label {
    display: inline;
  }

  .day-table td.notes:empty {
    display: none;
  }

  .day-table td.no-print {
    order: 3;
    flex: 1 1 100%;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .student-tag::part(remove-button) {
    display: none;
  }

  /* page-break-after/-inside are the legacy aliases WebKit's mobile print
     pipeline (iOS Safari/Chrome, both WebKit-based) needs alongside
     break-after/break-inside — it doesn't reliably honor the modern
     Fragmentation-spec properties on their own. */

  .months,
  .day-list {
    /* WebKit (Safari, and iOS Chrome which is WebKit under the hood)
       can't fragment a flex container's children across printed pages —
       it treats the whole flex container as one atomic block, which is
       what was actually forcing entire months/days to jump to the next
       page. `gap` still works the same on a block container, so this is
       print-only and doesn't touch the on-screen flex layout. */
    display: block;
  }

  .month h2,
  .day h3 {
    /* Glue each heading to what follows it — not the whole section below
       it, which would force any section too tall for the remaining page
       to jump entirely to the next page, leaving a mostly-blank page. */
    page-break-after: avoid;
    break-after: avoid-page;
  }

  .day-table {
    /* WebKit (Safari, and iOS Chrome which is WebKit under the hood)
       treats a border-collapse table as one atomic block during print
       and refuses to fragment it across pages — the whole table jumps to
       the next page if it doesn't fit what's left of the current one.
       Each cell already draws its own border-bottom, so collapsing isn't
       needed and switching to separate unblocks per-row fragmentation. */
    border-collapse: separate;
    border-spacing: 0;
  }

  .day-table {
    font-size: 11pt;
  }
}
</style>
